import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Sun, Cloud, Thermometer, Sunrise, Sunset, MapPin, Radar } from "lucide-react";

const DEFAULT_CITIES = ["Roma", "Milano", "Napoli", "Torino", "Firenze", "Bologna", "Palermo", "Bari", "Venezia", "Genova"];

export default function Weather() {
  const { lang } = useApp();
  const isIT = lang === "it";
  const [cities, setCities] = useState(DEFAULT_CITIES);
  const [city, setCity] = useState("Roma");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/cities").then(({ data }) => Array.isArray(data) && setCities(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get(`/weather/irradiance?city=${encodeURIComponent(city)}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [city]);

  const hourly = data?.today_hourly?.map((h) => ({
    hour: new Date(h.time).getHours(),
    radiation: h.radiation_w_m2,
    direct: h.direct_radiation_w_m2,
    temp: h.temperature_c,
    clouds: h.cloudcover_pct,
  })) || [];

  const peak = hourly.reduce((max, h) => h.radiation > max ? h.radiation : max, 0);
  const daily = data?.daily || {};

  return (
    <div className="min-h-screen bg-[#F9F8F6] grain-bg pb-16">
      <div className="max-w-7xl mx-auto px-6 pt-12">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="label-eco mb-3"><Sun size={12} className="inline mr-1" /> {isIT ? "Meteo · Irraggiamento" : "Weather · Irradiance"}</div>
            <h1 className="text-5xl font-black tracking-tight text-[#17301C]">
              {isIT ? "Irraggiamento solare" : "Solar irradiance"}
            </h1>
            <p className="text-lg text-[#3A4A3E] mt-2">
              {isIT ? "Dati reali dell'atmosfera per pianificare la produzione." : "Real atmospheric data to plan production."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <MapPin size={16} className="text-[#6E7A71]" />
            <select data-testid="weather-city" value={city} onChange={(e) => setCity(e.target.value)}
              className="px-4 py-2 rounded-lg border border-[#DDE0D8] bg-white text-sm font-semibold text-[#17301C]">
              {cities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-[#6E7A71] p-12">Caricamento...</div>
        ) : !data ? (
          <div className="card-surface p-12 text-center">{isIT ? "Dati non disponibili" : "No data"}</div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <StatCard icon={Sun} label={isIT ? "Picco irraggiamento" : "Peak irradiance"} value={peak.toFixed(0)} unit="W/m²" accent />
              <StatCard icon={Cloud} label={isIT ? "Radiazione oggi" : "Today's radiation"} value={daily.shortwave_radiation_sum?.[0]?.toFixed(1) || "-"} unit="kWh/m²" />
              <StatCard icon={Thermometer} label="Max/min °C" value={`${daily.temperature_2m_max?.[0]?.toFixed(0) || "-"} / ${daily.temperature_2m_min?.[0]?.toFixed(0) || "-"}`} />
              <StatCard icon={Sunrise} label={isIT ? "Alba / tramonto" : "Sunrise/sunset"} value={`${(daily.sunrise?.[0] || "").slice(11, 16)} / ${(daily.sunset?.[0] || "").slice(11, 16)}`} />
            </div>

            <div className="card-surface p-6 mb-6" data-testid="irradiance-chart">
              <div className="label-eco mb-4">{isIT ? "Irraggiamento orario oggi (W/m²)" : "Hourly irradiance today (W/m²)"}</div>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={hourly}>
                  <defs>
                    <linearGradient id="g-rad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#EAB308" stopOpacity={0.7} />
                      <stop offset="100%" stopColor="#EAB308" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="g-dir" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#17301C" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#17301C" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#DDE0D8" />
                  <XAxis dataKey="hour" stroke="#6E7A71" fontSize={11} />
                  <YAxis stroke="#6E7A71" fontSize={11} />
                  <Tooltip contentStyle={{ background: "#fff", border: "1px solid #DDE0D8", borderRadius: 8 }} />
                  <Area name={isIT ? "Radiazione globale" : "Global"} dataKey="radiation" stroke="#EAB308" strokeWidth={2} fill="url(#g-rad)" />
                  <Area name={isIT ? "Radiazione diretta" : "Direct"} dataKey="direct" stroke="#17301C" strokeWidth={2} fill="url(#g-dir)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="card-surface p-6" data-testid="weather-radar">
              <div className="flex items-center justify-between mb-4">
                <div className="label-eco flex items-center gap-2"><Radar size={14} /> {isIT ? "Radar meteo live" : "Live weather radar"}</div>
                <div className="text-xs text-[#6E7A71]">Windy.com</div>
              </div>
              <div className="rounded-xl overflow-hidden border border-[#DDE0D8]">
                <iframe
                  title="Weather Radar"
                  width="100%"
                  height="480"
                  src={`https://embed.windy.com/embed2.html?lat=${data.lat}&lon=${data.lon}&zoom=7&level=surface&overlay=radar&menu=&message=true&marker=&calendar=&pressure=&type=map&location=coordinates&detail=&metricWind=default&metricTemp=default&radarRange=-1`}
                  frameBorder="0"
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value, unit, accent }) => (
  <div className={`card-surface p-4 ${accent ? "bg-[#EAB308] border-[#EAB308]" : ""}`}>
    <div className="flex items-center gap-2 mb-2">
      <Icon size={13} className={accent ? "text-[#17301C]" : "text-[#6E7A71]"} />
      <div className={`text-[10px] uppercase tracking-widest font-bold ${accent ? "text-[#17301C]" : "text-[#6E7A71]"}`}>{label}</div>
    </div>
    <div className="text-2xl font-black font-mono-nums text-[#17301C]">
      {value}<span className="text-xs ml-1 opacity-70">{unit}</span>
    </div>
  </div>
);
